import {
  findConnectedGraphs,
  GraphPositions,
  RepositionGraph,
} from './layoutTypes';

type Edge = { source: string; target: string };

const MAX_COLUMN_WIDTH = 500;
const MAX_ROW_HEIGHT = 150;

// assign positions to generate a grid of nodes
// this function fills all the nodes passed in a new column
// and then calls itself for the children of the current nodes
// at the end grid will be filled with all the nodes.
// some node will be duplicated (if they are referenced multiple times iin the graph),
// but that will be handled by the caller.
// This function returns an array of arrays, where each array is a column
// and each element in the array is a node in that column
// if there are any circular loops in the graph, this function
// could go into infinite recursion, to prevent that we check for recursion level < 100
const assignPosition = (
  nodeIds: string[],
  edges: Edge[],
  recurseLevel = 0, // this is just to prevent infinite recursion
): string[][] => {
  if (recurseLevel > 100) {
    // eslint-disable-next-line no-console
    console.warn('graph went too deep, is there a circular loop?');
    return [];
  }

  if (nodeIds.length === 0) return [];

  // find dependent nodes to assign positions
  // we want childrenIds to be in the same order as nodeIds.
  const childrenIds: string[] = [];
  nodeIds.forEach((nodeId) => {
    const edgesInto = edges.filter(({ target }) => nodeId === target);
    childrenIds.push(...edgesInto.map(({ source }) => source));
  });

  const columns = assignPosition(childrenIds, edges, recurseLevel + 1);
  columns.push([...nodeIds]);
  return columns;
};

// remove duplicate node ids from columns
// we keep the node in the 1st column (lowest index) it appears in
const removeDuplicates = (columnsWithDuplicates: string[][]): string[][] => {
  const uniqueNodes = new Set<string>();
  const columns = columnsWithDuplicates.map((column) => {
    return column.filter((nodeId) => {
      if (uniqueNodes.has(nodeId)) {
        return false;
      }
      uniqueNodes.add(nodeId);
      return true;
    });
  });
  return columns;
};

type NodeGridPosition = { columnIndex: number; rowIndex: number };

// maps nodes to a grid.
// number of columns in the grid will be determined by the maximum number of nodes in a sequence
// number of rows in the grid will be determined by maximum number of nodes in a column
// then will simply assign each node a row and a column value.
const mapGridPositions = ({
  nodes,
  edges,
}: {
  nodes: string[];
  edges: { source: string; target: string }[];
}): Map<string, NodeGridPosition> => {
  // find all final nodes
  // from the graph. These are nodes that have no outgoing edges.
  const finalNodes = nodes.filter((nodeId) => {
    const outgoingEdges = edges.filter(({ source }) => source === nodeId);
    return outgoingEdges.length === 0;
  });

  const columnsWithDups = assignPosition(finalNodes, edges);
  const columns: string[][] = removeDuplicates(columnsWithDups);

  // we have columns, with each column being an array of nodeIds that go in that column
  // lets assign row and column values to each node
  const nodePositions = new Map<string, NodeGridPosition>();

  let maxRows = 0;
  columns.forEach((column, columnIndex) => {
    column.forEach((nodeId, rowIndex) => {
      nodePositions.set(nodeId, { columnIndex, rowIndex });
    });
    maxRows = Math.max(maxRows, column.length);
  });

  // double the number of rows to allow nodes to have more spacing.
  // graphs look better when adjucent columns do not have
  // nodes on the same row. we will update odd columns to use even rows
  // and even columns to use odd rows
  columns.forEach((column) => {
    column.forEach((nodeId) => {
      const nodePosition = nodePositions.get(nodeId);
      if (nodePosition) {
        const { rowIndex: oldRowIndex, columnIndex } = nodePosition;
        let rowIndex = oldRowIndex * 2;
        if (columnIndex % 2 === 0) rowIndex += 1;
        nodePositions.set(nodeId, {
          columnIndex,
          rowIndex,
        });
      }
    });
  });
  return nodePositions;
};

export const repositionGraph: RepositionGraph = ({
  nodes,
  edges,
  yMax,
  xMax,
}: {
  nodes: string[];
  edges: { source: string; target: string }[];
  xMax: number;
  yMax: number;
}): GraphPositions => {
  const nodePositions = new Map<string, NodeGridPosition>();

  // our original graph may have multiple disconnected graphs
  // each disconnected graph should be treated as a separate graph
  // and should start in a new row.
  const connectedGraphs = findConnectedGraphs({ nodes, edges });
  let maxRows = 0;
  let maxCols = 0;
  connectedGraphs.forEach((subNodes) => {
    const subPositions = mapGridPositions({
      nodes: subNodes,
      edges,
    });

    // subPositions start the rows from 0
    // we want to increment the rows so that they start from the last row
    // of the previous graph
    let maxSubRowIndex = 0;
    subPositions.forEach(({ rowIndex, columnIndex }, key) => {
      maxSubRowIndex = Math.max(maxSubRowIndex, rowIndex);
      maxCols = Math.max(maxCols, columnIndex + 1);
      nodePositions.set(key, {
        columnIndex,
        rowIndex: rowIndex + maxRows,
      });
    });
    maxRows += maxSubRowIndex + 1;
  });

  // use column/row information from the grid
  // to calculate the x and y position for each node
  const columnWidth = Math.max(xMax / (maxCols || 1), MAX_COLUMN_WIDTH);
  const rowHeight = Math.max(yMax / (maxRows || 1), MAX_ROW_HEIGHT);

  const flowPositions: GraphPositions = {};
  nodes.forEach((nodeId) => {
    // find the position of this node in the grid
    const nodePosition = nodePositions.get(nodeId);
    // its possible that node is not in the grid.
    // this can happen when there are no "final" nodes in graph
    // (only possible for circular graphs)
    // in that case we will just assign it to the first column and first row
    const columnIndex = nodePosition?.columnIndex ?? 0;
    const rowIndex = nodePosition?.rowIndex ?? 0;

    // lining up nodes in the same y coord causes them to crowd together
    // lets add some jitter to the y position
    const yJitter = (rowHeight / 3) * Math.random();

    flowPositions[nodeId] = {
      x: columnIndex * columnWidth + columnWidth / 2,
      y: rowIndex * rowHeight + rowHeight / 2 + yJitter,
    };
  });

  return flowPositions;
};
